/**
 * fetchWithTimeout — the only way this app talks to a remote host.
 *
 * WHY THIS EXISTS
 *
 * React Native's `fetch` has NO default timeout, and neither does the browser's.
 * A connection that is opened, sends its request, and then goes quiet — no
 * response, no RST, which is the ordinary behaviour of a mobile network handing
 * off between towers — produces a promise that never settles.
 *
 * A promise that never settles is not an inert failure in this app. Every busy
 * latch is released in a `.finally()` or a `finally {}` block, so a promise that
 * never settles is a latch that never releases: a spinner that spins forever, a
 * button that stays disabled, a press lock that never lifts. The user sees a
 * screen that has visibly stopped accepting input, with nothing thrown, nothing
 * logged, and nothing to retry. It is the same defect class as an undismissable
 * overlay, arriving through the network layer instead of the view tree.
 *
 * `lib/supabase.ts` has wrapped the Supabase client's fetch since round 25, but
 * that wrapper is installed on the client and therefore covers only calls made
 * through it. Everything else — Mapbox geocoding, reverse geocoding, the update
 * policy manifest — went out raw. This module is that same wrapper, extracted so
 * there is exactly one implementation and so `scripts/validate-input-liveness.mjs`
 * can require it.
 */

/** Matches the transient network failures worth one immediate retry. */
const TRANSIENT_NETWORK_ERROR = /Failed to fetch|Network request failed|Load failed/i

const DEFAULT_FETCH_TIMEOUT_MS = 15_000
/**
 * Supabase Storage mueve bytes, no JSON. Subir una foto de 1,5 MB desde una red
 * móvil lenta necesita más de 15 s, y a los 15 s el request se abortaba a mitad
 * de subida: la foto "fallaba", el submit del listado se rendía por timeout y el
 * anfitrión solo veía "revisá tu conexión". Las transferencias de Storage llevan
 * su propio presupuesto; el resto sigue con el de JSON.
 */
const STORAGE_FETCH_TIMEOUT_MS = 120_000
const RETRY_DELAY_MS = 350

/** El presupuesto de una corrida: explícito, o ampliado si va a Storage. */
function resolveTimeoutMs(url: Parameters<typeof fetch>[0], requested: number | undefined): number {
  if (typeof requested === 'number') return requested
  const raw =
    typeof url === 'string'
      ? url
      : url instanceof URL
        ? url.href
        : ((url as { url?: string } | undefined)?.url ?? '')
  return raw.includes('/storage/v1/') ? STORAGE_FETCH_TIMEOUT_MS : DEFAULT_FETCH_TIMEOUT_MS
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchOnce(
  url: Parameters<typeof fetch>[0],
  init: Parameters<typeof fetch>[1],
  timeoutMs: number,
) {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  // Respect the caller's signal if provided — aborting upstream must still work.
  if (init?.signal) {
    init.signal.addEventListener('abort', () => controller.abort())
  }
  return fetch(url as RequestInfo, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timeout),
  )
}

export interface FetchWithTimeoutOptions {
  /** Defaults to DEFAULT_FETCH_TIMEOUT_MS. */
  timeoutMs?: number
  /** One immediate retry on a transient network error. Defaults to true. */
  retry?: boolean
}

/**
 * `fetch`, but it always settles.
 *
 * Aborts after `timeoutMs` (default 15s) and — unless `retry` is false — retries
 * once after a short delay when the failure looks transient. The retry is
 * deliberately not applied to timeouts: a request that already burned the full
 * budget should surface, not double it.
 */
export async function fetchWithTimeout(
  url: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1],
  options: FetchWithTimeoutOptions = {},
): Promise<Response> {
  const { retry = true } = options
  const timeoutMs = resolveTimeoutMs(url, options.timeoutMs)
  try {
    return await fetchOnce(url, init, timeoutMs)
  } catch (error) {
    if (!retry) throw error
    if (init?.signal?.aborted) throw error

    const message = error instanceof Error ? error.message : String(error)
    if (!TRANSIENT_NETWORK_ERROR.test(message)) throw error

    await sleep(RETRY_DELAY_MS)
    return fetchOnce(url, init, timeoutMs)
  }
}

/**
 * The `typeof fetch` shaped adapter Supabase's `global.fetch` option needs.
 * Same behaviour, signature narrowed to two arguments.
 */
export const fetchWithTimeoutAdapter: typeof fetch = (url, init) =>
  // The cast bridges two competing `fetch` declarations in this project's libs
  // (React Native's and the DOM's, which differ on whether `URL` is accepted).
  // Both are accepted at runtime by both platforms.
  fetchWithTimeout(url as Parameters<typeof fetchWithTimeout>[0], init)
